import { useState, useEffect } from 'react';
import { getPayouts, requestPayout, MIN_PAYOUT_AMOUNT } from '../../services/payout.service';

const fmt = (n) => `${Number(n || 0).toLocaleString()} Ar`;

const statusMeta = (status) => {
  switch (status) {
    case 'paid':
      return { label: 'Payé', cls: 'payout-status-paid' };
    case 'cancelled':
      return { label: 'Annulé', cls: 'payout-status-cancelled' };
    default:
      return { label: 'En attente', cls: 'payout-status-pending' };
  }
};

const cleanPhone = (value) => String(value || '').replace(/\D/g, '').slice(0, 10);

const isValidMgMobile = (value) => /^03\d{8}$/.test(cleanPhone(value));

const WithdrawModal = ({ show, onClose, netRevenue = 0, totalPaid = 0, mvolaNumber = '', onSaveNumber = null, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setAmount('');
    setError('');
    setDone(null);
    setPhone('');
    setPhoneError('');
    setHistoryLoading(true);
    getPayouts()
      .then((rows) => setHistory(Array.isArray(rows) ? rows : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [show]);

  // Plein page mobile comme la création de release : verrouille le scroll + Escape
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  if (!show) return null;

  // La demande ne débite rien : le solde n'est réduit qu'au versement validé (statut payé).
  const pendingSum = history
    .filter((p) => p.status === 'pending')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const available = Math.max(0, Number(netRevenue || 0) - Number(totalPaid || 0) - pendingSum);
  const recent = history.slice(0, 3);
  const destNumber = phone || mvolaNumber || '';
  const hasNumber = isValidMgMobile(destNumber);

  const numericAmount = Math.floor(Number(String(amount).replace(/\s/g, '')) || 0);
  const tooSmall = numericAmount > 0 && numericAmount < MIN_PAYOUT_AMOUNT;
  const tooBig = numericAmount > available;
  const insufficient = available < MIN_PAYOUT_AMOUNT;
  const canSubmit = numericAmount >= MIN_PAYOUT_AMOUNT && !tooBig && !loading && hasNumber && !insufficient;

  const setQuick = (value) => {
    setError('');
    setAmount(String(Math.min(Math.floor(Number(value) || 0), available)));
  };

  const handleAmountChange = (raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 9);
    if (!digits) {
      setAmount('');
      setError('');
      return;
    }
    // Plafonne la saisie au solde disponible : impossible de taper plus
    const clamped = Math.min(parseInt(digits, 10), available);
    setAmount(String(clamped));
    setError('');
  };

  const savePhone = async () => {
    const cleaned = cleanPhone(phone);
    if (!isValidMgMobile(cleaned)) {
      setPhoneError('Numéro invalide : 10 chiffres commençant par 03 (ex. 034 12 345 67).');
      return;
    }
    if (!onSaveNumber) {
      setPhoneError('Enregistrement impossible pour le moment.');
      return;
    }
    setPhoneError('');
    setPhoneSaving(true);
    try {
      const ok = await onSaveNumber(cleaned);
      if (ok === false) throw new Error('Enregistrement impossible. Réessayez.');
      setPhone(cleaned);
    } catch (err) {
      setPhoneError(err.message || 'Enregistrement impossible. Réessayez.');
    } finally {
      setPhoneSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!hasNumber) {
      setError('Ajoutez d’abord votre numéro Mobile Money ci-dessous.');
      return;
    }
    if (numericAmount < MIN_PAYOUT_AMOUNT) {
      setError(`Montant minimum de retrait : ${fmt(MIN_PAYOUT_AMOUNT)}`);
      return;
    }
    if (numericAmount > available) {
      setError('Montant supérieur au solde disponible.');
      return;
    }
    setLoading(true);
    try {
      const created = await requestPayout(numericAmount);
      setDone(created);
      if (onSuccess) onSuccess(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="release-modal-backdrop withdraw-backdrop" role="presentation" onClick={loading ? undefined : onClose}>
      <div className="release-modal dna-withdraw" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="release-modal-header dna-withdraw-head">
          <div>
            <p className="release-modal-kicker">Retrait Mobile Money</p>
            <h3 className="release-modal-title">{done ? 'Demande envoyée' : 'Demander un retrait'}</h3>
          </div>
          <button className="btn-close-popup" onClick={onClose} aria-label="Fermer" type="button">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {!done ? (
          <>
            <p className="dna-withdraw-intro">
              Versement sur votre numéro enregistré, traité sous 24 à 72h ouvrées.
            </p>

            <div className="dna-withdraw-balance">
              <span>Solde disponible</span>
              <strong>{fmt(available)}</strong>
            </div>

            <form onSubmit={submit}>
              <label className="dna-withdraw-label">
                Montant à retirer
                <div className="dna-withdraw-amount">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="5 000"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    disabled={loading}
                  />
                  <span>Ar</span>
                </div>
              </label>

              <div className="dna-withdraw-quick">
                {[5000, 10000, 20000].map((v) => (
                  <button key={v} type="button" onClick={() => setQuick(v)} disabled={loading || v > available || insufficient}>
                    {v.toLocaleString()}
                  </button>
                ))}
                <button type="button" onClick={() => setQuick(available)} disabled={loading || insufficient}>
                  Tout
                </button>
              </div>

              {insufficient && (
                <div className="dna-withdraw-hint">
                  Solde insuffisant pour un retrait (minimum {fmt(MIN_PAYOUT_AMOUNT)})
                </div>
              )}

              {(tooSmall || tooBig) && (
                <div className="dna-withdraw-hint">
                  {tooSmall
                    ? `Minimum ${fmt(MIN_PAYOUT_AMOUNT)}`
                    : 'Montant supérieur au solde disponible'}
                </div>
              )}

              <div className="dna-withdraw-dest">
                <i className="fa-solid fa-mobile-screen-button" />
                {hasNumber ? (
                  <span>
                    <small>Destination</small>
                    <b>{destNumber.replace(/(\d{3})(\d{2})(\d{3})(\d{2})/, '$1 $2 $3 $4')}</b>
                  </span>
                ) : (
                  <span className="dna-withdraw-phone">
                    <small>Numéro Mobile Money</small>
                    <span className="dna-withdraw-phone-row">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="034 12 345 67"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value.replace(/[^\d\s]/g, '').slice(0, 13)); setPhoneError(''); }}
                        disabled={phoneSaving || loading}
                      />
                      <button type="button" onClick={savePhone} disabled={phoneSaving || loading}>
                        {phoneSaving ? '…' : 'Ajouter'}
                      </button>
                    </span>
                    {phoneError && <em>{phoneError}</em>}
                  </span>
                )}
              </div>

              <p className="dna-withdraw-note">
                Simple demande : votre solde n’est débité qu’après validation du versement par notre équipe.
              </p>

              {error && <div className="auth-error" style={{ marginTop: 14 }}>{error}</div>}

              <button type="submit" className="button button-crimson form-submit" disabled={!canSubmit} style={{ width: '100%' }}>
                {loading ? 'Envoi…' : `Confirmer le retrait${numericAmount >= MIN_PAYOUT_AMOUNT && !tooBig ? ` · ${fmt(numericAmount)}` : ''}`}
                <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} />
              </button>
            </form>

            <div className="dna-withdraw-history">
              <span className="content-kicker">Demandes récentes</span>
              {historyLoading ? (
                <p>Chargement…</p>
              ) : recent.length === 0 ? (
                <p>Aucune demande pour le moment.</p>
              ) : (
                recent.map((p) => {
                  const meta = statusMeta(p.status);
                  return (
                    <div className="dna-withdraw-row" key={p.id}>
                      <b>{fmt(p.amount)}</b>
                      <small>{p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : ''}</small>
                      <span className={`payout-status ${meta.cls}`}>{meta.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="dna-withdraw-success">
            <div className="locked-icon"><i className="fa-solid fa-check" /></div>
            <span className="content-kicker">Demande envoyée</span>
            <h2>{fmt(done.amount || numericAmount)}</h2>
            <p>Votre demande de retrait est en cours de traitement. Vous recevrez le versement sous 24 à 72h ouvrées.</p>
            <button type="button" className="button button-crimson" onClick={onClose} style={{ width: '100%' }}>
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawModal;
